import React from 'react';
import { useNavigate } from 'react-router-dom';
import { getMediaUrl } from '../api';

const ProductsPage = ({
  products,
  loadProducts,
  addStock,
  deleteProduct,
}) => {
  const [stockInputs, setStockInputs] = React.useState({});
  const [filters, setFilters] = React.useState({
    name: '',
    category: '',
    brand: '',
    minPrice: '',
    maxPrice: '',
  });
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(10);
  const navigate = useNavigate();
  const categoryOptions = React.useMemo(
    () =>
      Array.from(
        new Set(
          (products || [])
            .map((p) => String(p?.category || '').trim())
            .filter(Boolean)
        )
      ).sort((a, b) => a.localeCompare(b)),
    [products]
  );

  const handleStockChange = (productId, value) => {
    setStockInputs((prev) => ({ ...prev, [productId]: value }));
  };

  const handleAddStock = async (productId) => {
    const qty = Number(stockInputs[productId] || 0);
    if (!qty || qty <= 0) {
      alert('Enter stock quantity greater than 0');
      return;
    }
    await addStock(productId, qty);
    setStockInputs((prev) => ({ ...prev, [productId]: '' }));
  };

  React.useEffect(() => {
    setPage(1);
  }, [filters, pageSize]);

  const filteredProducts = React.useMemo(() => {
    const name = filters.name.trim().toLowerCase();
    const category = filters.category.trim().toLowerCase();
    const brand = filters.brand.trim().toLowerCase();
    const min = filters.minPrice === '' ? null : Number(filters.minPrice);
    const max = filters.maxPrice === '' ? null : Number(filters.maxPrice);
    return (products || []).filter((p) => {
      if (name && !String(p.name || '').toLowerCase().includes(name)) return false;
      if (category && !String(p.category || '').toLowerCase().includes(category)) return false;
      if (brand && !String(p.brand || '').toLowerCase().includes(brand)) return false;
      const price = Number(p.main_price || 0);
      if (min !== null && price < min) return false;
      if (max !== null && price > max) return false;
      return true;
    });
  }, [products, filters]);

  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pagedProducts = filteredProducts.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <div className="card">
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <h3>Products</h3>
        <div className="row">
          <button className="secondary" onClick={() => navigate('/admin/products/new')}>Add Product</button>
          <button className="secondary" onClick={loadProducts}>Refresh</button>
        </div>
      </div>
      <div className="filters">
        <div className="filter-row">
          <div>
            <label>Name</label>
            <input value={filters.name} onChange={(e) => setFilters({ ...filters, name: e.target.value })} placeholder="Search name" />
          </div>
          <div>
            <label>Category</label>
            <select
              value={filters.category}
              onChange={(e) => setFilters({ ...filters, category: e.target.value })}
            >
              <option value="">All Categories</option>
              {categoryOptions.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
          <div>
            <label>Brand</label>
            <input value={filters.brand} onChange={(e) => setFilters({ ...filters, brand: e.target.value })} placeholder="Search brand" />
          </div>
          <div>
            <label>Min Price</label>
            <input type="number" value={filters.minPrice} onChange={(e) => setFilters({ ...filters, minPrice: e.target.value })} />
          </div>
          <div>
            <label>Max Price</label>
            <input type="number" value={filters.maxPrice} onChange={(e) => setFilters({ ...filters, maxPrice: e.target.value })} />
          </div>
          <div>
            <label>Page Size</label>
            <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>
        <div className="filter-actions">
          <div className="muted">Showing {pagedProducts.length} of {filteredProducts.length}</div>
          <div className="pager">
            <button className="secondary" disabled={currentPage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</button>
            <span className="pager-info">Page {currentPage} of {totalPages}</span>
            <button className="secondary" disabled={currentPage >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Next</button>
          </div>
        </div>
      </div>
      <div className="list">
        {pagedProducts.map((p) => (
          <div key={p._id} className="list-item product-card">
            <div className="product-main">
              <div className="product-thumb">
                {p.main_image || (Array.isArray(p.images) && p.images[0]) ? (
                  <img
                    src={getMediaUrl(p.main_image || p.images[0])}
                    alt={p.name || 'Product'}
                  />
                ) : (
                  <div className="product-thumb-fallback">No Image</div>
                )}
              </div>
              <div className="product-info">
                <div className="row">
                  <strong>{p.name}</strong>
                  <span className="badge">{p.brand || 'Brand'}</span>
                  <span className="badge-soft">{p.category || 'Category'}</span>
                  {p.sub_category ? <span className="badge-soft">{p.sub_category}</span> : null}
                </div>
                <div className="product-meta">
                  <div><span className="muted">Price</span> {'\u20B9'}{p.main_price || 0}</div>
                  <div><span className="muted">Discount</span> {p.discount_percentage || 0}%</div>
                  <div><span className="muted">Remaining</span> {p.quantity || 0}</div>
                </div>
                {Array.isArray(p.colors) && p.colors.length ? (
                  <div className="badge-row">
                    {p.colors.slice(0, 6).map((c) => (
                      <span key={`${p._id}-${c}`} className="badge-pill">{c}</span>
                    ))}
                    {p.colors.length > 6 ? (
                      <span className="badge-pill">+{p.colors.length - 6} more</span>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>
            <div className="row product-actions">
              <input
                placeholder="Add stock"
                type="number"
                value={stockInputs[p._id] || ''}
                onChange={(e) => handleStockChange(p._id, e.target.value)}
              />
              <button onClick={() => handleAddStock(p._id)}>Add Stock</button>
              <button className="secondary" onClick={() => navigate(`/admin/products/edit/${p._id}`)}>Edit</button>
              <button className="secondary" onClick={() => deleteProduct(p._id)}>Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProductsPage;
